import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { BinIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { useCustomerStorageStore } from "@store/customerStorage/customerStorage";
import { type IUseModalProps } from "@store/modal/modal";
import { genericCustomerStorageRequest } from "@services/customerStorage/services";
import type {
  CustomerStorageItemType,
  ICustomerStorageItemAction,
} from "@services/customerStorage/types";

interface IRemoveModalProps extends IUseModalProps {
  action: ICustomerStorageItemAction;
  name: string;
  itemType: CustomerStorageItemType;
  onSuccess?: () => void;
  invalidateQueryKey?: string[];
}

export const RemoveModal: React.FC<IRemoveModalProps> = ({
  action,
  name,
  itemType,
  onSuccess,
  invalidateQueryKey,
  ...rest
}) => {
  const { t } = useTranslation();
  const modal = useModal(rest);
  const { currentFolderUrl } = useCustomerStorageStore();
  const queryClient = useQueryClient();

  const { mutateAsync: removeFile, isPending } = useMutation({
    mutationFn: () => genericCustomerStorageRequest({ requestProps: action }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["customerStorage", currentFolderUrl],
      });
      void queryClient.invalidateQueries({
        queryKey: ["customerStorage", "search"],
      });

      if (invalidateQueryKey) {
        void queryClient.invalidateQueries({
          queryKey: invalidateQueryKey,
        });
      }

      modal.close();
      onSuccess?.();
    },
  });

  const submitRemove = async () => {
    await removeFile().catch((error) => {
      toast.error(t("toast.error"));
      console.error(error);
    });
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: BinIcon, color: "red" }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t(`customerStorage.actions.${itemType}.remove.buttons.cancel`)}
          </Button>
          <Button
            onClick={submitRemove}
            className="w-full"
            isLoading={isPending}
          >
            {t(`customerStorage.actions.${itemType}.remove.buttons.delete`)}
          </Button>
        </React.Fragment>
      }
      title={t(`customerStorage.actions.${itemType}.remove.title`)}
      subtitle={t(`customerStorage.actions.${itemType}.remove.subtitle`, {
        fileName: name,
      })}
      className="md:max-w-xl"
    ></Modal>
  );
};
